import React, { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";
import TopImage from "@/components/TopImage";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

const CALENDLY = "https://calendly.com/hooverdan56/30min";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const t = useContent("contact");
  useSeo("contact", {
    title: "Contact — Quantum Results",
    description:
      "Wherever you are, you're welcome here. Start a quiet conversation with Dan Hoover.",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Your message has been received. Thank you.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("We couldn't reach the server just now — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <TopImage src={IMG.contact.hero.src} alt={IMG.contact.hero.alt} credit={IMG.contact.hero.credit} testId="contact-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Contact")}
        title={t("hero_title", "We'd love to hear from you.")}
        lede={t("hero_lede", "Whether you're simply exploring, curious about an experience, or ready for a deeper conversation, you're welcome here.")}
        testId="contact-hero"
      />

      <section className="container-narrow pb-24" data-testid="contact-body">
        <div className="grid gap-14 lg:grid-cols-2">
          {/* Form + email */}
          <div>
            <div className="eyebrow mb-4">Send a message</div>
            {done ? (
              <div
                className="rounded-lg border border-brand-turquoise/30 bg-brand-turquoise/5 p-8"
                data-testid="contact-success"
              >
                <p className="font-serif text-[24px] text-brand-ink">Thank you.</p>
                <p className="mt-3 text-[15px] text-brand-ink/70">
                  Your message has arrived. Dan will respond personally as soon
                  as he is able.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-[12px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-3 text-[15px] focus:outline-none focus:border-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-3 text-[15px] focus:outline-none focus:border-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">Message</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-3 text-[15px] focus:outline-none focus:border-brand-blue resize-y"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send Message"} <span aria-hidden>→</span>
                </button>
              </form>
            )}

            <div className="mt-10 pt-8 border-t border-brand-rule/70">
              <div className="eyebrow mb-4">Or reach us directly</div>
              <a
                href="mailto:info@quantumresults.com"
                data-testid="contact-direct-email"
                className="inline-flex items-center gap-3 text-brand-ink hover:text-brand-blue transition-colors"
              >
                <Mail size={16} />
                <span className="font-serif text-[20px]">info@quantumresults.com</span>
              </a>
            </div>
          </div>

          {/* Calendly */}
          <div>
            <div className="eyebrow mb-4">Or schedule a conversation</div>
            <div className="rounded-lg border border-brand-rule/70 bg-white overflow-hidden shadow-[0_20px_60px_-40px_rgba(26,37,64,0.35)]">
              <iframe
                title="Schedule with Dan"
                src={`${CALENDLY}?hide_gdpr_banner=1&background_color=eef2f7&text_color=1a2540&primary_color=1a4c8e`}
                className="w-full h-[680px]"
                data-testid="contact-calendly-iframe"
              />
            </div>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-calendly-fallback"
              className="mt-4 inline-block text-[13px] text-brand-blue hover:text-brand-ink"
            >
              Open scheduling in a new tab ↗
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
